import type { TalentEffect, TalentNode } from '../../../../shared/CharacterTypes';
import { getAbilityData } from '../../../../shared/content/AbilityDataAdapter';
import type { TomeTone } from '../../primitives/tome';

const EFFECT_LABELS: Record<string, string> = {
  strength: 'Strength', intelligence: 'Intelligence', dexterity: 'Dexterity', luck: 'Luck',
  endurance: 'Endurance', wisdom: 'Wisdom', agility: 'Agility', defense: 'Defense',
  attack: 'Attack', magicAttack: 'Magic attack', physicalAttack: 'Physical attack',
  hp: 'Health', maxHealth: 'Maximum health', mp: 'Mana', maxMana: 'Maximum mana',
  speed: 'Movement speed', criticalChance: 'Critical chance', criticalRate: 'Critical chance',
  criticalDamage: 'Critical damage', damage: 'Damage', cooldown: 'Cooldown',
};

export function talentEffectLabel(effect: TalentEffect): string {
  const label = EFFECT_LABELS[effect.target] ?? getAbilityData(effect.target)?.name
    ?? effect.target.replace(/[_-]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
  const percentage = ['criticalChance', 'criticalRate', 'criticalDamage'].includes(effect.target);
  const numeric = percentage ? `${Number((effect.value * 100).toFixed(2))}%` : String(effect.value);
  const value = `${numeric}${effect.perRank ? ' per rank' : ''}`;
  if (effect.type === 'unlock_ability') return `Unlocks ${label}`;
  if (effect.type === 'ability_mod') return `${label}: ${value}`;
  if (effect.type === 'passive') return `${label}: ${value}`;
  return `${effect.value >= 0 ? '+' : ''}${value} ${label}`;
}

/** Authored node art first; otherwise choose an effect cue without pretending it is an item. */
export function talentIcon(node: TalentNode): string {
  if (node.icon) return node.icon;
  const target = node.effects[0]?.target ?? '';
  if (['hp', 'maxHealth', 'endurance'].includes(target)) return '♥';
  if (['defense'].includes(target)) return '🛡';
  if (['attack', 'strength', 'physicalAttack'].includes(target)) return '⚔';
  if (['criticalChance', 'criticalRate', 'dexterity', 'agility'].includes(target)) return '◎';
  if (['mp', 'maxMana', 'intelligence', 'wisdom', 'magicAttack'].includes(target)) return '✦';
  return '◇';
}

export type TalentState = 'mastered' | 'ready' | 'learned' | 'open' | 'locked';

/** How each state reads on the page: the entry's rule and its rank chip. */
export const TALENT_TONE: Record<TalentState, TomeTone> = {
  mastered: 'completed', ready: 'ready', learned: 'active', open: 'default', locked: 'locked',
};

/** The same checks that gate the server's allocation: rank, prerequisites, then points. */
export function talentState(node: TalentNode, allNodes: Record<string, TalentNode>, skillPoints: number): TalentState {
  if (node.currentRank >= node.maxRank) return 'mastered';
  if (!node.prerequisites.every(id => (allNodes[id]?.currentRank ?? 0) > 0)) return 'locked';
  if (skillPoints >= node.cost) return 'ready';
  return node.currentRank > 0 ? 'learned' : 'open';
}

/** A tree's talents by their step along the prerequisites, in authored order within each tier. */
export function talentTiers(nodes: readonly TalentNode[], allNodes: Record<string, TalentNode>): TalentNode[][] {
  const depth = (node: TalentNode, seen: ReadonlySet<string>): number => {
    const parents = node.prerequisites.filter(id => allNodes[id] && !seen.has(id));
    if (parents.length === 0) return 0;
    return 1 + Math.max(...parents.map(id => depth(allNodes[id], new Set([...seen, id]))));
  };
  const tiers: TalentNode[][] = [];
  for (const node of nodes) (tiers[depth(node, new Set([node.id]))] ??= []).push(node);
  return tiers.filter(Boolean);
}

export function progressionRefusal(reason?: string): string {
  switch (reason) {
  case 'prerequisite_locked': return 'Learn the required talent first.';
  case 'max_rank': return 'This talent has reached its highest rank.';
  case 'not_enough_points': return 'You do not have enough talent points.';
  case 'not_enough_gold': return 'You do not have enough gold.';
  case 'nothing_to_respec': return 'There are no talents to reset.';
  case 'persistence_failed': return 'Your changes could not be saved. Please try again.';
  default: return 'This change could not be completed. Please try again.';
  }
}
